'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Show, SignInButton, UserButton } from '@clerk/nextjs';
import { WebcamPixelGrid } from '@/components/ui/webcam-pixel-grid';

// Client portal landing — Moose Tech Solutions branding.
// Visual parity with the Legion launcher (gold accents, dark canvas,
// animated background, brand-mark in top-left) but copy + CTAs are
// tuned for the customer audience: "Welcome to Moose Tech Solutions"
// instead of "Welcome to the Legion".
export default function ClientLandingPage() {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsTouch(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        background: '#0A0A0B',
        color: '#FFF',
        overflow: 'hidden',
        fontFamily: 'var(--font-body, var(--font-geist-sans))',
      }}
    >
      {/* Animated WebcamPixelGrid background */}
      <div style={{ position: 'absolute', inset: 0 }}>
        <WebcamPixelGrid
          gridCols={60}
          gridRows={40}
          maxElevation={50}
          motionSensitivity={0.25}
          elevationSmoothing={0.2}
          colorMode="webcam"
          backgroundColor="#030303"
          mirror={true}
          gapRatio={0.05}
          invertColors={false}
          darken={0.6}
          borderColor="#ffffff"
          borderOpacity={0.06}
          className="w-full h-full"
        />
      </div>

      {/* Vignette overlay — same gradient pattern as Legion's Landing.tsx */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at center, rgba(10,10,11,0) 0%, rgba(10,10,11,0.45) 55%, rgba(10,10,11,0.85) 100%)',
        }}
      />

      {/* Top brand strip */}
      <header
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 28px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Image
            src="/assets/logo-primary.png"
            alt="Moose Tech Solutions"
            width={32}
            height={32}
            priority
          />
          <span
            style={{
              fontFamily: 'var(--font-mono, var(--font-geist-mono))',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#E8C16A',
              fontWeight: 700,
            }}
          >
            MTS · Client Portal
          </span>
        </div>
        <Show when="signed-in">
          <UserButton />
        </Show>
      </header>

      {/* Hero */}
      <main
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 76px)',
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-brand, var(--font-legion))',
            fontSize: 'clamp(40px, 8vw, 88px)',
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            margin: 0,
            color: '#FFFFFF',
          }}
        >
          Welcome to{' '}
          <span
            style={{
              background: 'linear-gradient(135deg,#F6E6B8 0%,#E8C16A 35%,#B8862E 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Moose Tech Solutions.
          </span>
        </h1>
        <p
          style={{
            maxWidth: 560,
            margin: '24px auto 0',
            fontSize: 16,
            lineHeight: 1.55,
            color: '#A8A6A0',
          }}
        >
          Your client portal — manage your apps, pricing, customer organizations,
          users, billing, and support in one place. Real-time sync with the team
          building your products.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 14,
            marginTop: 36,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Show when="signed-out">
            <SignInButton mode="redirect" forceRedirectUrl="/portal">
              <button
                type="button"
                style={{
                  background:
                    'linear-gradient(135deg,#F6E6B8 0%,#E8C16A 35%,#B8862E 90%)',
                  color: '#0A0A0B',
                  fontFamily: 'var(--font-display, var(--font-manrope))',
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  border: 0,
                  borderRadius: 999,
                  padding: '14px 28px',
                  cursor: 'pointer',
                  boxShadow:
                    '0 10px 30px rgba(232,193,106,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
                }}
              >
                Sign in to portal
              </button>
            </SignInButton>
          </Show>
          <Show when="signed-in">
            <Link
              href="/portal"
              style={{
                background:
                  'linear-gradient(135deg,#F6E6B8 0%,#E8C16A 35%,#B8862E 90%)',
                color: '#0A0A0B',
                fontFamily: 'var(--font-display, var(--font-manrope))',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                borderRadius: 999,
                padding: '14px 28px',
                textDecoration: 'none',
                boxShadow:
                  '0 10px 30px rgba(232,193,106,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
              }}
            >
              Enter portal →
            </Link>
          </Show>
        </div>

        <div
          style={{
            marginTop: 56,
            fontFamily: 'var(--font-mono, var(--font-geist-mono))',
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#74747D',
          }}
        >
          {isTouch ? 'Tap to begin' : 'Client Portal · Live'}
        </div>
      </main>
    </div>
  );
}
